import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Progress,
  Spinner,
  Switch,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useGame } from "../../contexts/GameContext";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

export const HostLobby: React.FC = () => {
  const { createGame, ping, sessionGames, loadData } = useGame();
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [enableMaxPlayers, setEnableMaxPlayers] = useState(false);
  const [maxPlayers, setMaxPlayers] = useState(8);
  const navigate = useNavigate();
  const toast = useToast();

  const onCreate = async () => {
    setIsCreating(true);
    try {
      ping();
      const gameId = await createGame(undefined, enableMaxPlayers ? maxPlayers : null);
      if (gameId) {
        navigate(`/host/${gameId}`);
      } else {
        toast({
          title: "Failed to create game",
          description: "Please make sure Firebase emulators are running or check your connection.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error creating game:", error);
      toast({
        title: "Failed to create game",
        description: error instanceof Error ? error.message : "An unexpected error occurred. Make sure Firebase emulators are running.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Center
      width="100%"
      minHeight="100%"
      backgroundColor="jeopardy.blue"
      p="2em"
    >
      <VStack spacing={8} width="100%" maxWidth="400px">
        <MotionBox
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Text
            fontSize={{ base: "3em", md: "5em" }}
            textStyle="jeoparty"
            textAlign="center"
          >
            JEOPARTY
          </Text>
        </MotionBox>

        <MotionBox
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          width="100%"
        >
          <VStack
            spacing={6}
            bg="rgba(0,0,0,0.3)"
            p={6}
            borderRadius="lg"
            width="100%"
          >
            <Text textStyle="clue" fontSize="1.5em" textAlign="center">
              Host a New Game
            </Text>

            <FormControl>
              <HStack justify="space-between" width="100%">
                <FormLabel htmlFor="enable-max-players" mb="0" textStyle="clue">
                  Limit Players
                </FormLabel>
                <Switch
                  id="enable-max-players"
                  isChecked={enableMaxPlayers}
                  onChange={(e) => setEnableMaxPlayers(e.target.checked)}
                  colorScheme="yellow"
                />
              </HStack>
            </FormControl>

            {enableMaxPlayers && (
              <FormControl>
                <FormLabel textStyle="clue" fontSize="sm">Max Players</FormLabel>
                <NumberInput
                  value={maxPlayers}
                  onChange={(_, val) => setMaxPlayers(val || 2)}
                  min={2}
                  max={20}
                  bg="white"
                  borderRadius="md"
                >
                  <NumberInputField color="black" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            )}

            <Button
              width="100%"
              size="lg"
              isLoading={isCreating}
              onClick={onCreate}
              background="jeopardy.gold"
              color="black"
              _hover={{ background: "yellow.400" }}
              height="60px"
            >
              <Text textStyle="clue" fontSize="xl">
                🎮 Create Game
              </Text>
            </Button>

            {/* Temporary button to load games into Firestore */}
            {window.location.hostname === "localhost" && (
              <Button
                width="100%"
                size="md"
                isLoading={isLoadingData}
                onClick={async () => {
                  setIsLoadingData(true);
                  try {
                    await loadData();
                    toast({
                      title: "Games loaded!",
                      description: "Games have been added to Firestore.",
                      status: "success",
                      duration: 3000,
                    });
                  } catch (error) {
                    console.error("Error loading games:", error);
                    toast({
                      title: "Error loading games",
                      description: error instanceof Error ? error.message : "Failed to load games",
                      status: "error",
                      duration: 5000,
                    });
                  } finally {
                    setIsLoadingData(false);
                  }
                }}
                background="jeopardy.lightBlue"
                color="white"
                _hover={{ background: "blue.400" }}
              >
                <Text textStyle="clue" fontSize="sm">
                  📥 Load Games to Database (Dev Only)
                </Text>
              </Button>
            )}
          </VStack>
        </MotionBox>

        {sessionGames.length > 0 && (
          <MotionBox
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            width="100%"
          >
            <VStack
              spacing={3}
              bg="rgba(0,0,0,0.2)"
              p={4}
              borderRadius="lg"
              width="100%"
            >
              <Text textStyle="clue" fontSize="sm">
                Your Recent Games
              </Text>
              {sessionGames.map((game) => (
                <Flex
                  key={game.gameId}
                  width="100%"
                  justify="space-between"
                  align="center"
                  bg="rgba(255,255,255,0.1)"
                  p={3}
                  borderRadius="md"
                >
                  <VStack align="start" spacing={0}>
                    <Text textStyle="clue" fontSize="lg">
                      {game.gameId.toUpperCase()}
                    </Text>
                    {game.topic && (
                      <Text textStyle="clue" fontSize="xs" opacity={0.7}>
                        {game.topic}
                      </Text>
                    )}
                  </VStack>
                  {game.status === "ready" ? (
                    <Button
                      size="sm"
                      bg="jeopardy.lightBlue"
                      onClick={() => navigate(`/host/${game.gameId}`)}
                    >
                      <Text textStyle="clue" fontSize="sm">
                        Continue
                      </Text>
                    </Button>
                  ) : game.status === "error" ? (
                    <Text textStyle="clue" fontSize="sm" color="red.300">
                      Error
                    </Text>
                  ) : (
                    <VStack align="end" spacing={2} width="100%">
                      <Flex align="center" gap={2} width="100%" justify="flex-end">
                        <Spinner size="sm" color="white" />
                        <Text textStyle="clue" fontSize="sm" opacity={0.7}>
                          {game.status === "generating"
                            ? "Generating..."
                            : "Pending..."}
                        </Text>
                      </Flex>
                      {game.ttsProgress && game.ttsProgress.total > 0 && game.enableTTS !== false && (
                        <VStack align="stretch" spacing={1} width="100%">
                          <Progress
                            value={(game.ttsProgress.current / game.ttsProgress.total) * 100}
                            size="sm"
                            colorScheme="yellow"
                            bg="rgba(255,255,255,0.2)"
                            borderRadius="md"
                            width="100%"
                          />
                          <Text textStyle="clue" fontSize="xs" opacity={0.7} textAlign="right">
                            {game.ttsProgress.message || `${game.ttsProgress.current} / ${game.ttsProgress.total} audio files`}
                          </Text>
                        </VStack>
                      )}
                    </VStack>
                  )}
                </Flex>
              ))}
            </VStack>
          </MotionBox>
        )}

        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
        >
          <Text textStyle="clue" fontSize="xs" textAlign="center" opacity={0.5}>
            Create a game, then cast to your TV!
          </Text>
        </MotionBox>
      </VStack>
    </Center>
  );
};
